import React, { useState, useEffect, useCallback, useRef } from "react";
import { Card, Empty, Space, Row, Col, Typography, Tag, Button, Spin, Alert, Collapse } from "antd";
import { ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined } from "@ant-design/icons";
import PageHeader from "../../components/common/PageHeader";
import SourceViewer from "../../components/SourceViewer";
import { SourceProvider } from "../../hooks/useSource";
import demandConsult from "../../services/demandConsult";

const { Text } = Typography;


interface DemandResult {
  demandId: string;
  deleted: boolean;
  deletedDate: string | null;
  found: boolean;
  foundIn: string | null; // Ambiente onde foi encontrado (ex: "codilo-dt-saj", "datateam-prod")
  data: any;
  error: string | null;
  attempts: number;
  loading: boolean;
}

const MAX_ATTEMPTS = 10;
const RETRY_DELAY = 30000; // 30 segundos entre tentativas

/**
 * Verifica se a resposta indica que a demanda foi deletada.
 */
const isDemandDeleted = (response: any): { deleted: boolean; message?: string; date?: string } => {
  if (!response) return { deleted: false };

  // Verifica se tem statusCode 400 e body com mensagem de demanda deletada
  if (response.statusCode === 400 && response.body) {
    try {
      const body = typeof response.body === 'string' ? JSON.parse(response.body) : response.body;
      const message = body.message || '';
      // Verifica se contém "deletada" (case insensitive) e NÃO contém "not found"
      if (message.toLowerCase().includes('deletada') && !message.toLowerCase().includes('not found')) {
        // Tenta extrair a data da mensagem
        const dateMatch = message.match(/(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2})/);
        return {
          deleted: true,
          message: message,
          date: dateMatch ? dateMatch[1] : undefined,
        };
      }
    } catch (e) {
      // Se não conseguir fazer parse, verifica diretamente na string
      if (typeof response.body === 'string') {
        const bodyLower = response.body.toLowerCase();
        if (bodyLower.includes('deletada') && !bodyLower.includes('not found')) {
          const dateMatch = response.body.match(/(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2})/);
          return {
            deleted: true,
            message: response.body,
            date: dateMatch ? dateMatch[1] : undefined,
          };
        }
      }
    }
  }

  // Também verifica se a mensagem está diretamente no response
  if (response.message) {
    const messageLower = response.message.toLowerCase();
    if (messageLower.includes('deletada') && !messageLower.includes('not found')) {
      const dateMatch = response.message.match(/(\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2})/);
      return {
        deleted: true,
        message: response.message,
        date: dateMatch ? dateMatch[1] : undefined,
      };
    }
  }

  return { deleted: false };
};

/**
 * Componente para visualizar um demand individual e monitorar seu status.
 */
const DemandViewer: React.FC<{ demandId: string; onRetry: (demandId: string) => void }> = ({
  demandId,
  onRetry,
}) => {
  const [result, setResult] = useState<DemandResult>({
    demandId,
    deleted: false,
    deletedDate: null,
    found: false,
    foundIn: null,
    data: null,
    error: null,
    attempts: 0,
    loading: false,
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isDeletedRef = useRef<boolean>(false);

  // NOTE: Removed UUID/regex-based validation. Acceptance of "demanda" vs other products
  // is decided using the `type` field returned in the found data (see below).

  /**
   * Para a busca.
   */
  const stopSearch = useCallback(() => {
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Busca o demand usando a rota buscar-fonte que busca em todos os ambientes.
   */
  const searchDemand = useCallback(
    async (attempt: number = 1) => {
      // Verifica se a demanda já foi identificada como deletada antes de continuar
      if (isDeletedRef.current) {
        stopSearch();
        return;
      }
  
      if (attempt > MAX_ATTEMPTS) {
        setResult((prev) => ({
          ...prev,
          loading: false,
          error: `Não encontrado após ${MAX_ATTEMPTS} tentativas`,
          attempts: MAX_ATTEMPTS,
        }));
        return;
      }
  
      setResult((prev) => ({
        ...prev,
        loading: true,
        attempts: attempt,
      }));
  
      try {
        // Usa a rota buscar-fonte que busca em todos os ambientes do DT
        const response = await demandConsult.getDemand({ sourceId: demandId, api: "all" });
  
        // Verifica se a demanda está deletada
        const deletedInfo = isDemandDeleted(response);
        if (deletedInfo.deleted) {
          isDeletedRef.current = true;
          stopSearch();
  
          setResult((prev) => ({
            ...prev,
            deleted: true,
            deletedDate: deletedInfo.date || null,
            found: false,
            data: null,
            error: deletedInfo.message || "Demanda deletada",
            attempts: attempt,
            loading: false,
          }));
          return;
        }
  
        // ---- LÓGICA DE PRODUTO / CAPTURA-DT-* ----
        // const hasData = response && response.data;
        // const key = String(response?.key ?? "");
  
        // // Só considera encontrado se for captura-dt-*
        // console.log("key", { key });
        // const isCapturaDt = key.startsWith("captura-dt-");
        // const found = !!hasData && isCapturaDt;
        // console.log("found", { found },  isCapturaDt, {isCapturaDt});
  
        const key = String(response?.key ?? "");
        const hasData = !!response?.data;

        const isProdutoValido =
          key.startsWith("captura-dt-") || key === "codilo-dt-saj";

        const found = hasData && isProdutoValido;

        if (found) {
          // É uma demanda válida de captura-dt-*
          setResult((prev) => ({
            ...prev,
            found: true,
            foundIn: key,       // captura-dt-saj, captura-dt-projuris, captura-dt-peticiona
            data: response.data,
            error: null,
            attempts: attempt,
            loading: false,
          }));
  
          stopSearch();
          return;
        }
  
        if (hasData && !isProdutoValido) {
          stopSearch();
          setResult((prev) => ({
            ...prev,
            found: false,
            foundIn: key,
            data: null,
            error: "Demanda não encontrada",
            attempts: MAX_ATTEMPTS,
            loading: false,
          }));
          return;
        }        
  
        // Não encontrou ainda (sem dados), segue tentativas
        if (attempt < MAX_ATTEMPTS) {
          if (intervalRef.current) {
            clearTimeout(intervalRef.current);
          }
          intervalRef.current = setTimeout(() => {
            searchDemand(attempt + 1);
          }, RETRY_DELAY);
        } else {
          setResult((prev) => ({
            ...prev,
            found: false,
            data: null,
            error: `Não encontrado após ${MAX_ATTEMPTS} tentativas`,
            attempts: MAX_ATTEMPTS,
            loading: false,
          }));
          stopSearch();
        }
      } catch (err: any) {
        const errorResponse = err.response?.data;
        const deletedInfo = isDemandDeleted(errorResponse);
  
        if (deletedInfo.deleted) {
          isDeletedRef.current = true;
          stopSearch();
  
          setResult((prev) => ({
            ...prev,
            deleted: true,
            deletedDate: deletedInfo.date || null,
            found: false,
            data: null,
            error: deletedInfo.message || "Demanda deletada",
            attempts: attempt,
            loading: false,
          }));
          return;
        }
  
        const isNotFound =
          err.response?.status === 404 ||
          (err.response?.data?.statusCode === 400 &&
            err.response?.data?.body &&
            typeof err.response.data.body === "string" &&
            err.response.data.body.toLowerCase().includes("not found"));
  
        if (attempt < MAX_ATTEMPTS) {
          if (intervalRef.current) {
            clearTimeout(intervalRef.current);
          }
          intervalRef.current = setTimeout(() => {
            searchDemand(attempt + 1);
          }, RETRY_DELAY);
        } else {
          setResult((prev) => ({
            ...prev,
            found: false,
            data: null,
            error: isNotFound
              ? `Não encontrado após ${MAX_ATTEMPTS} tentativas`
              : err.response?.data?.message || err.message || `Erro após ${MAX_ATTEMPTS} tentativas`,
            attempts: MAX_ATTEMPTS,
            loading: false,
          }));
          stopSearch();
        }
      }
    },
    [demandId, stopSearch]
  );
  
  /**
   * Inicia a busca usando buscar-fonte.
   */
  useEffect(() => {
    // Reseta a ref de deletada quando o componente é montado
    isDeletedRef.current = false;

    // Inicia a busca (validação de formato por regex removida — a distinção
    // de "demanda" será feita a partir do campo `type` retornado pela API).
    searchDemand(1);

    // Cleanup: limpa o intervalo quando o componente é desmontado
    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
      isDeletedRef.current = false;
    };
  }, [searchDemand]);

  /**
   * Handler para retentar a busca.
   */
  const handleRetry = () => {
    // Limpa o intervalo anterior se existir
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }

    // Reseta a ref de deletada
    isDeletedRef.current = false;

    // Reinicia a busca
    searchDemand(1);
  };

  const status =
    result.deleted
      ? "error"
      : result.found
        ? "success"
        : !result.loading && result.attempts >= MAX_ATTEMPTS
          ? "warning"
          : "pending";

  const statusColor =
    status === "success"
      ? "success"
      : status === "error"
        ? "error"
        : status === "warning"
          ? "error"
          : "processing";

  // Painel só pode ser expansível quando houver sucesso.
  // Ou seja: somente status === "success" permite abrir; qualquer outro status mantém fechado.
  const disabled = status !== "success";
  
  const getCnjFromData = (data: any) => {
    if (!data) return null;
  
    // 1) cnj no body[0]
    if (Array.isArray(data.body) && data.body.length > 0 && data.body[0].cnj) {
      return data.body[0].cnj;
    }
  
    // 2) cnj em pushIds (formato CNJ com pontos/hífens)
    if (Array.isArray(data.pushIds) && data.pushIds.length > 0 && data.pushIds[0].cnj) {
      return data.pushIds[0].cnj;
    }
  
    // 3) fallback genérico
    const candidates = [
      "cnj",
      "cnjId",
      "cnjNumber",
      "cnj_number",
      "nuprocesso",
      "numero",
    ];
  
    for (const key of candidates) {
      const v = (data as any)[key];
      if (v) return v;
    }
  
    return null;
  };
  

  const cnjValue = getCnjFromData(result.data);

  return (
    <Collapse>
      <Collapse.Panel
        header={
          <div style={{ width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <Text copyable code style={{ fontSize: "14px" }}>
                {demandId}
              </Text>
              {/* Caso: id informado não corresponde a uma demanda (não encontrado) */}
              {status === "warning" ? (
                <Tag color={statusColor} icon={<CloseCircleOutlined />}>
                  Demanda não encontrada.
                </Tag>
              ) : (
                <>
                  <Tag color={statusColor} icon={status === "success" ? <CheckCircleOutlined /> : <ClockCircleOutlined />}>
                    {status === "success" ? "Encontrado" : status === "error" ? (result.deleted ? "Demanda deletada" : "Erro") : "Buscando..."}
                  </Tag>
                  {/* Mostra CNJ no header quando encontrado (ou placeholder amigável) */}
                  {status === "success" && (
                    <>
                      <Tag color="cyan" style={{ marginLeft: 4 }}>
                        {String(cnjValue ?? "Sem CNJ")}
                      </Tag>
              
                      <Tag color="blue" style={{ marginLeft: 4 }}>
                        {result.foundIn
                          ?.replace("codilo-dt-", "")
                          .replace("datateam-", "DT ")
                          .toUpperCase() || "SEM ORIGEM"}
                      </Tag>
                    </>
                  )}
                </>
              )}
              {/* {result.foundIn && (
                <Tag color="blue">
                  {result.foundIn.replace("codilo-dt-", "").replace("datateam-", "DT ").toUpperCase()}
                </Tag>
              )} */}
              {((status === "error") && result.error) && (
                <Text strong style={{ marginLeft: 4, color: "#dc4446" }}>
                  {result.error}
                </Text>
              )}
            </div>
          </div>
        }
        key={demandId}
        disabled={disabled}
      >
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          {/* Informação de demanda deletada */}
          {result.deleted && result.deletedDate && (
            <Alert
              message="Demanda deletada"
              description={`Demanda deletada em ${result.deletedDate}.`}
              type="error"
              showIcon
            />
          )}

          {/* Status da busca - só mostra quando não encontrado ou pendente */}
          {/* {!result.found && (
            <div style={{ padding: "16px", borderRadius: "4px" }}>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Text strong>Status da Busca</Text>
                  <Space>
                    {result.loading ? (
                      <Spin size="small" />
                    ) : result.attempts >= MAX_ATTEMPTS ? (
                      <Tag color="error">Não encontrado</Tag>
                    ) : (
                      <Tag color="processing">Tentando...</Tag>
                    )}
                    <Button
                      size="small"
                      icon={<ReloadOutlined />}
                      onClick={handleRetry}
                      disabled={result.loading}
                    >
                      Recarregar
                    </Button>
                  </Space>
                </div>
                {result.attempts > 0 && (
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Tentativa {result.attempts}/{MAX_ATTEMPTS}
                  </Text>
                )}
                {result.foundIn && (
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    Encontrado em: <Text strong>{result.foundIn}</Text>
                  </Text>
                )}
                {result.error && (
                  <Text type="danger" style={{ fontSize: "12px" }}>
                    {result.error}
                  </Text>
                )}
              </Space>
            </div>
          )} */}

          {/* Mensagem de erro após 10 tentativas */}
          {/* {!result.found && result.attempts >= MAX_ATTEMPTS && (
            <Alert
              message="Demand não encontrado"
              description="Após 10 tentativas, o demand não foi encontrado em nenhum ambiente do DT. Use o botão 'Retentar' para tentar novamente."
              type="warning"
              showIcon
            />
          )} */}

          {/* Exibir resultados encontrados usando SourceViewer */}
          {result.found && (
            <div style={{ marginTop: 16 }}>
              <Text strong style={{ marginBottom: 8, display: "block" }}>
                Dados encontrados:
              </Text>
              <SourceViewer sourceId={demandId} />
            </div>
          )}
        </Space>
      </Collapse.Panel>
    </Collapse>
  );
};

/**
 * Página de monitoramento de demands do Datateam.
 *
 * Exibe todos os demands que estão sendo buscados usando a rota buscar-fonte.
 */
const DatateamVisualizarMonitoringContent: React.FC = () => {
  const [demandIds, setDemandIds] = useState<string[]>([]);

  useEffect(() => {
    // Carregar IDs do localStorage
    const storedIds = localStorage.getItem("datateamVisualizarIds");
    if (storedIds) {
      try {
        const parsed = JSON.parse(storedIds);
        if (Array.isArray(parsed)) {
          setDemandIds(parsed);
        }
      } catch (e) {
        console.error("Erro ao carregar IDs do localStorage:", e);
      }
    }
  }, []);

  const handleRetry = (demandId: string) => {
    // Esta função será passada para o DemandViewer
    // O DemandViewer já gerencia seu próprio estado de retry
  };

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <PageHeader title="Monitoramento de Demands - Datateam" />

        {demandIds.length === 0 ? (
          <Empty
            description="Nenhum demand sendo monitorado. Volte para a página de Visualizar Vários para adicionar."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <Row gutter={[16, 16]}>
            {demandIds.map((demandId, idx) => (
              <Col xs={24} sm={24} md={24} lg={24} xl={24} key={idx}>
                <DemandViewer demandId={demandId} onRetry={handleRetry} />
              </Col>
            ))}
          </Row>
        )}
      </Space>
    </Card>
  );
};

/**
 * Componente principal da página DatateamVisualizarMonitoring.
 */
const DatateamVisualizarMonitoring: React.FC = () => {
  return (
    <SourceProvider>
        <DatateamVisualizarMonitoringContent />
      </SourceProvider>
  );
};

export default DatateamVisualizarMonitoring;
